import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, X, Eye, ToggleLeft, ToggleRight } from 'lucide-react';
import { useAuth } from '../../App';
import { getTemplates, createTemplate, updateTemplate, deleteTemplate, toggleTemplate } from '../../api/templates';
import AdminLayout from './AdminLayout';

const AdminTemplates = () => {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(null);
  const [formData, setFormData] = useState({
    name: '', subject: '', template_type: 'custom', is_auto: false, is_active: true,
    background_color: '#ffffff', header_text: '', body_text: '', button_text: '', button_color: '#10b981', button_url: '', footer_text: ''
  });

  const fetchTemplates = useCallback(async () => {
    try {
      const data = await getTemplates(token);
      setTemplates(data);
    } catch (err) {} finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) fetchTemplates(); }, [token, fetchTemplates]);

  const openModal = (template = null) => {
    if (template) {
      setEditTemplate(template);
      setFormData({ ...template });
    } else {
      setEditTemplate(null);
      setFormData({ name: '', subject: '', template_type: 'custom', is_auto: false, is_active: true,
        background_color: '#ffffff', header_text: '', body_text: '', button_text: '', button_color: '#10b981', button_url: '', footer_text: '' });
    }
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editTemplate) {
        await updateTemplate(token, editTemplate.id, formData);
      } else {
        await createTemplate(token, formData);
      }
      setModalOpen(false);
      fetchTemplates();
    } catch (err) { alert('Błąd zapisu'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Usunąć szablon?')) return;
    try {
      await deleteTemplate(token, id);
      fetchTemplates();
    } catch (err) {}
  };

  const toggleActive = async (id) => {
    try {
      await toggleTemplate(token, id);
      fetchTemplates();
    } catch (err) {}
  };

  const templateTypes = {
    'purchase_confirmation': 'Potwierdzenie zakupu',
    'newsletter_welcome': 'Newsletter powitalny',
    'free_gift': 'Gratis dla klienta',
    'custom_checkout': 'Custom checkout',
    'contact_reply': 'Odpowiedź na kontakt',
    'admin_notification': 'Powiadomienie admina',
    'promotion': 'Promocja/Nowość',
    'custom': 'Własny szablon'
  };

  return (
    <AdminLayout title="Szablony Email">
      <div className="flex justify-between items-center mb-6">
        <p className="text-[#9CA3AF]">{templates.length} szablonów</p>
        <button onClick={() => openModal()} className="btn-primary" data-testid="add-template-btn">
          <Plus size={18} /> Dodaj szablon
        </button>
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="spinner"></div></div> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(t => (
            <div key={t.id} className="glass-card p-5" data-testid={`template-card-${t.id}`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-white font-medium">{t.name}</h3>
                  <p className="text-[#6B7280] text-sm">{templateTypes[t.template_type] || t.template_type}</p>
                </div>
                <button onClick={() => toggleActive(t.id)} className={`text-2xl ${t.is_active ? 'text-[#10b981]' : 'text-[#6B7280]'}`}>
                  {t.is_active ? <ToggleRight /> : <ToggleLeft />}
                </button>
              </div>
              <p className="text-[#9CA3AF] text-sm mb-3 line-clamp-2">{t.subject}</p>
              <div className="flex items-center gap-2 mb-4">
                {t.is_auto && <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">Auto</span>}
                <span className={`px-2 py-0.5 text-xs rounded ${t.is_active ? 'bg-[#10b981]/20 text-[#10b981]' : 'bg-[#6B7280]/20 text-[#6B7280]'}`}>
                  {t.is_active ? 'Aktywny' : 'Wyłączony'}
                </span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setPreviewOpen(t)} className="btn-ghost flex-1 py-2 text-sm">
                  <Eye size={16} /> Podgląd
                </button>
                <button onClick={() => openModal(t)} className="btn-ghost flex-1 py-2 text-sm">
                  <Edit size={16} /> Edytuj
                </button>
                <button onClick={() => handleDelete(t.id)} className="p-2 text-[#6B7280] hover:text-red-400">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content max-w-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-6 border-b border-white/5">
              <h2 className="text-xl font-bold text-white">{editTemplate ? 'Edytuj szablon' : 'Nowy szablon'}</h2>
              <button onClick={() => setModalOpen(false)} className="text-[#6B7280] hover:text-white"><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Nazwa szablonu *</label>
                  <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full" required />
                </div>
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Typ</label>
                  <select value={formData.template_type} onChange={e => setFormData({...formData, template_type: e.target.value})} className="w-full">
                    {Object.entries(templateTypes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Temat emaila</label>
                <input type="text" value={formData.subject} onChange={e => setFormData({...formData, subject: e.target.value})} className="w-full" placeholder="Dziękujemy za zakup!" />
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Nagłówek</label>
                <input type="text" value={formData.header_text} onChange={e => setFormData({...formData, header_text: e.target.value})} className="w-full" placeholder="Cześć {{imie}}!" />
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Treść</label>
                <textarea value={formData.body_text} onChange={e => setFormData({...formData, body_text: e.target.value})} className="w-full" rows={4} placeholder="Zmienne: {{imie}}, {{produkty}}, {{cena}}, {{link_pobierania}}" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Tekst przycisku</label>
                  <input type="text" value={formData.button_text} onChange={e => setFormData({...formData, button_text: e.target.value})} className="w-full" placeholder="Pobierz e-booki" />
                </div>
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Kolor przycisku</label>
                  <div className="flex gap-2">
                    <input type="color" value={formData.button_color} onChange={e => setFormData({...formData, button_color: e.target.value})} className="w-12 h-10 rounded cursor-pointer" />
                    <input type="text" value={formData.button_color} onChange={e => setFormData({...formData, button_color: e.target.value})} className="flex-1" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">URL przycisku</label>
                <input type="text" value={formData.button_url} onChange={e => setFormData({...formData, button_url: e.target.value})} className="w-full" placeholder="{{link_pobierania}}" />
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Stopka</label>
                <textarea value={formData.footer_text} onChange={e => setFormData({...formData, footer_text: e.target.value})} className="w-full" rows={2} />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.is_auto} onChange={e => setFormData({...formData, is_auto: e.target.checked})} className="checkbox-custom" />
                  <span className="text-white">Automatyczny</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} className="checkbox-custom" />
                  <span className="text-white">Aktywny</span>
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setModalOpen(false)} className="btn-ghost flex-1">Anuluj</button>
                <button type="submit" className="btn-primary flex-1">Zapisz</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewOpen && (
        <div className="modal-overlay" onClick={() => setPreviewOpen(null)}>
          <div className="modal-content max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b border-white/5">
              <h2 className="text-lg font-bold text-white">Podgląd: {previewOpen.name}</h2>
              <button onClick={() => setPreviewOpen(null)} className="text-[#6B7280] hover:text-white"><X size={24} /></button>
            </div>
            <div className="p-6 bg-[#f3f4f6]">
              <div style={{ fontFamily: 'Arial, sans-serif', maxWidth: '500px', margin: '0 auto', background: '#ffffff', borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{ background: '#10b981', padding: '20px 30px' }}>
                  <span style={{ color: '#fff', fontWeight: 'bold', fontSize: '18px' }}>MrMinds</span>
                </div>
                <div style={{ padding: '30px' }}>
                  <h3 style={{ color: '#1a1a1a', marginTop: 0 }}>{previewOpen.header_text || 'Nagłówek'}</h3>
                  <p style={{ color: '#374151', whiteSpace: 'pre-line', lineHeight: 1.6 }}>{previewOpen.body_text || 'Treść wiadomości...'}</p>
                  {previewOpen.button_text && (
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                      <span style={{ background: previewOpen.button_color || '#10b981', color: '#fff', padding: '12px 24px', borderRadius: '8px', display: 'inline-block', fontWeight: 'bold' }}>
                        {previewOpen.button_text}
                      </span>
                    </div>
                  )}
                </div>
                {previewOpen.footer_text && (
                  <div style={{ background: '#f3f4f6', padding: '16px 30px', borderTop: '1px solid #e5e7eb' }}>
                    <p style={{ color: '#6b7280', fontSize: '12px', margin: 0, whiteSpace: 'pre-line' }}>{previewOpen.footer_text}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminTemplates;